import React, { useMemo, useState } from 'react';
import { Quote, Lightbulb, Heart, RefreshCw } from 'lucide-react';
import { getDailyMotivator, motivators } from '../data/coachTips';

const TYPE_META = {
  quotes: { icon: Quote, label: 'Today\'s reminder', accent: 'enf-role-warning' },
  cues: { icon: Lightbulb, label: 'Coach\'s cue', accent: 'enf-role-info' },
  mission: { icon: Heart, label: 'Why this matters', accent: 'enf-accent' },
};

// Dashboard daily motivator widget. Auto-rotates daily; user can tap "shuffle" to roll a new one.
const DailyMotivator = () => {
  const [shuffleKey, setShuffleKey] = useState(0);
  const today = useMemo(() => new Date().toDateString(), []);

  const motivator = useMemo(() => {
    if (shuffleKey === 0) return getDailyMotivator(today);
    // Manual shuffle: random pick across all motivator types
    const types = Object.keys(motivators);
    const t = types[Math.floor(Math.random() * types.length)];
    const list = motivators[t];
    return { type: t, item: list[Math.floor(Math.random() * list.length)] };
  }, [shuffleKey, today]);

  const meta = TYPE_META[motivator.type];
  const Icon = meta.icon;
  const isQuote = motivator.type === 'quotes';

  const accentBg = meta.accent === 'enf-accent' ? 'bg-enf-accent/10' : meta.accent === 'enf-role-warning' ? 'bg-enf-role-warning/10' : 'bg-enf-role-info/10';
  const accentBorder = meta.accent === 'enf-accent' ? 'border-enf-accent-fg/40' : meta.accent === 'enf-role-warning' ? 'border-enf-role-warning/40' : 'border-enf-role-info/40';
  const accentText = meta.accent === 'enf-accent' ? 'text-enf-accent-fg' : meta.accent === 'enf-role-warning' ? 'text-enf-role-warning' : 'text-enf-role-info';

  return (
    <div
      className={`relative bg-enf-surface-deep border ${accentBorder} rounded-xl p-6 overflow-hidden`}
      data-testid="daily-motivator"
    >
      <div className={`absolute top-0 left-0 right-0 h-px ${accentBg}`} />
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 rounded-md ${accentBg} flex items-center justify-center`}>
            <Icon className={`w-4 h-4 ${accentText}`} />
          </div>
          <div>
            <div className={`text-[10px] uppercase tracking-[0.3em] font-bold ${accentText}`}>
              {motivator.item.tag || meta.label}
            </div>
            <div className="text-xs text-enf-text-muted">Refreshes daily</div>
          </div>
        </div>
        <button
          onClick={() => setShuffleKey((k) => k + 1)}
          className="w-8 h-8 rounded-md bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary flex items-center justify-center transition-colors"
          title="Shuffle"
          data-testid="motivator-shuffle"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {isQuote ? (
        <div>
          <blockquote className="font-display text-2xl md:text-3xl tracking-wide leading-tight mb-3">
            &ldquo;{motivator.item.text}&rdquo;
          </blockquote>
          <div className="text-xs text-enf-text-muted uppercase tracking-widest">— {motivator.item.source}</div>
        </div>
      ) : (
        <p className="text-base md:text-lg text-enf-text-primary leading-relaxed">{motivator.item.body}</p>
      )}
    </div>
  );
};

export default DailyMotivator;
